import { useState, useEffect, useMemo, useRef } from "react";
import {
  useNavigate,
  useNavigationType,
  useLocation,
} from "react-router-dom";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";

import { Title } from "../../components/Title.jsx";
import { LoadingBox } from "../../components/home/Box.jsx";
import BoxCustomizer from "../../components/home/BoxCustomizer.jsx";
import {
  ShipmentsTableSearch,
  LoadingTable,
} from "../../components/table/shipments/ShipmentTable.jsx";
import { homeShipmentStatusOptions } from "../../components/table/shipments/Filter";
import "../../App.scss";
import "../../stylesheets/Home.scss";
import GlobalAlert from "../../components/GlobalAlert.jsx";
import { debounce } from "lodash";

const Home = () => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();

  // ─── Clear Home’s filters whenever you PUSH into “/” ────────
  if (navigationType === "PUSH" && location.pathname === "/") {
    sessionStorage.removeItem("globalFilter");
    sessionStorage.removeItem("statusFilter");
    sessionStorage.removeItem("accountFilter");
    sessionStorage.removeItem("dateFilter");
    sessionStorage.removeItem("modeFilter");
  }

  const cookies = new Cookies();
  const primary = cookies.get("primary");
  const name =
    primary && primary !== "undefined"
      ? primary
      : cookies.get("name_user");
  const token = cookies.get("token_user");
  const role = Number(cookies.get("role_user") || cookies.get("role"));
  if (!token) navigate("/login");
  if (role === 11) navigate("/shipments", { replace: true });

  useEffect(() => {
    if (!token) {
      navigate("/login");
    } else if (role === 11) {
      navigate("/shipments", { replace: true });
    }
  }, [token, role, navigate]);

  const URL = process.env.REACT_APP_API_URL + "/user";
  const initial_url = `${URL}/shipments`;

  // ─── Filter State (hydrate) ─────────────────────────────────
  const [filterState, setFilterState] = useState(() => {
    const global = sessionStorage.getItem("globalFilter") || "";

    // Status: now stored as JSON array
    let status = [];
    try {
      const raw = JSON.parse(sessionStorage.getItem("statusFilter") || "[]");
      if (Array.isArray(raw)) status = raw;
      else if (typeof raw === "string" && raw) status = [raw];
      else if (raw?.value) status = [raw.value];
    } catch (e) {}

    // Account: now stored as JSON array
    let account = [];
    try {
      const raw = JSON.parse(sessionStorage.getItem("accountFilter") || "[]");
      if (Array.isArray(raw)) account = raw;
      else if (typeof raw === "string" && raw) account = [raw];
      else if (raw?.value) account = [raw.value];
    } catch (e) {}

    const date = JSON.parse(sessionStorage.getItem("dateFilter") || "null");
    const mode = sessionStorage.getItem("modeFilter") || "";   // NEW
    return { global, status, account, date, mode };
  });

  // ─── Persist filters when they change ───────────────────────
  useEffect(() => {
    sessionStorage.setItem("globalFilter", filterState.global);
  }, [filterState.global]);
  useEffect(() => {
    sessionStorage.setItem(
      "statusFilter",
      JSON.stringify(filterState.status)
    );
  }, [filterState.status]);
  useEffect(() => {
    sessionStorage.setItem(
      "accountFilter",
      JSON.stringify(filterState.account)
    );
  }, [filterState.account]);
  useEffect(() => {
    sessionStorage.setItem("dateFilter", JSON.stringify(filterState.date));
  }, [filterState.date]);

  useEffect(() => {
    sessionStorage.setItem("modeFilter", filterState.mode);
  }, [filterState.mode]);

  // ─── Loading/counts ─────────────────────────────────────────
  const [loading, setLoading] = useState({
    shipments: true,
    needUploads: true,
    beforeDate: true,
    invoices: false,
    exam: true,
    withoutDO: true,
  });
  const [shipmentsNeeding, setShipmentsNeeding] = useState();
  const [globalNotification, setGlobalNotification] = useState([]);
  const [shipmentsBeforeDate, setShipmentsBeforeDate] = useState(0);
  const [shipmentsExam, setShipmentsExam] = useState(0);
  const [shipments7501, setShipments7501] = useState(0);
  const [shipmentsFDAExam, setShipmentsFDAExam] = useState(0);
  const [shipmentsWithoutDO, setShipmentsWithoutDO] = useState(0);
  const [shipmentsPendingReview, setShipmentsPendingReview] = useState(0);
  const [shipmentsMissingInfo, setShipmentsMissingInfo] = useState(0);

  // ─── Shipments data ──────────────────────────────────────────
  const [shipments, setShipments] = useState();
  const [shipmentsUrl, setShipmentsUrl] = useState(
    `${initial_url}?source=home&summary=1`
  );

  // ─── Notifications ───────────────────────────────────────────
  const getNotification = async () => {
    try {
      const res = await fetch(`${URL}/get_notification`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.length > 0) {
        setGlobalNotification((old) => [...old, data[0]]);
      }
    } catch (err) {
      console.error(err);
    }
  };
  const handleNotificationDismiss = () => {
    setGlobalNotification([]);
  };

  // ─── Fetch shipments ─────────────────────────────────────────
  const getShipments = async () => {
    try {
      const res = await fetch(shipmentsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (res.ok) {
        const data = await res.json();
        setShipmentsNeeding(data?.pending_docs);
        setShipments7501(data?.["7501_to_review"]);
        setShipmentsBeforeDate(data?.before_summary);
        setShipmentsExam(data?.under_exam);
        setShipmentsFDAExam(data?.fda_exam);
        setShipmentsWithoutDO(data?.shipments_without_do);
        setShipmentsPendingReview(data?.pending_review);
        setShipmentsMissingInfo(data?.missing_info || 0);
        setShipments(data);
        setLoading({
          shipments: false,
          needUploads: false,
          beforeDate: false,
          exam: false,
          invoices: false,
          withoutDO: false,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load shipments");
    }
  };

  // ─── Greeting ────────────────────────────────────────────────
  const setGreeting = (user_name) => {
    const h = new Date().getHours();
    if (h < 12) return `Good morning, ${user_name || "User"}`;
    if (h < 18) return `Good afternoon, ${user_name || "User"}`;
    return `Good evening, ${user_name || "User"}`;
  };

  // ─── Run on URL change ────────────────────────────────────────
  useEffect(() => {
    if (role === 11) return;
    getNotification();
    getShipments();
  }, [shipmentsUrl, role]);

  // ─── Pagination ──────────────────────────────────────────────
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem("homePage");
    return saved ? parseInt(saved, 10) : 1;
  });
  const perPage = 100;
  useEffect(() => {
    sessionStorage.setItem("homePage", String(page));
  }, [page]);

  // Reset to page 1 when filter changes
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    setPage(1);
  }, [
    filterState.global,
    filterState.status,
    filterState.account,
    filterState.date,
    filterState.mode,
  ]);

  const gotoPreviousPage = () => setPage((p) => Math.max(p - 1, 1));
  const gotoNextPage = () => {
    if (!shipments?.pagination) return;
    setPage((p) => Math.min(p + 1, shipments.pagination.last_page));
  };
  const total = shipments?.pagination?.total || 0;
  const last = shipments?.pagination?.last_page || 1;
  const shown = Math.min(page * perPage, total);

  // ─── Build query from filters ────────────────────────────────
  const computedFilter = useMemo(() => {
    const f = {};
    // Join arrays with commas for the API
    const statusStr = Array.isArray(filterState.status)
      ? filterState.status.join(",")
      : filterState.status || "";
    const accountStr = Array.isArray(filterState.account)
      ? filterState.account.join(",")
      : filterState.account || "";

    if (statusStr) f.status = statusStr;
    if (accountStr) f.account = accountStr;
    if (filterState.global) f.search = filterState.global;
    if (filterState.mode) f.mode_of_transport = filterState.mode;   // NEW
    if (filterState.date?.length === 2) {
      f.date_from = filterState.date[0];
      f.date_to = filterState.date[1];
    }
    return f;
  }, [filterState]);

  const changeFilter = useMemo(
    () =>
      debounce((f) => {
        const params = ["source=home", "summary=1", `page=${page}`, `per_page=${perPage}`];
        if (f.status) params.push(`status=${f.status}`);
        if (f.account) params.push(`account_no=${f.account}`);
        if (f.search) params.push(`search=${encodeURIComponent(f.search)}`);
        if (f.mode_of_transport) params.push(`mode_of_transport=${f.mode_of_transport}`); // NEW
        if (f.date_from) params.push(`date_from=${f.date_from}`);
        if (f.date_to) params.push(`date_to=${f.date_to}`);
        setShipmentsUrl(`${initial_url}?${params.join("&")}`);
      }, 500),
    [initial_url, page, perPage]
  );

  useEffect(() => {
    changeFilter(computedFilter);
  }, [computedFilter]);

  if (role === 11) return null;

  return (
    <>
      <div className="home">
        <Title title={setGreeting(name)} />
      </div>
      <div id="home" className="page-content">
        <GlobalAlert
          notification={globalNotification}
          onDismiss={handleNotificationDismiss}
        />

        <BoxCustomizer
          counts={{
            documents_required: shipmentsNeeding,
            forms_7501: shipments7501,
            cbp_hold: shipmentsExam,
            fda_hold: shipmentsFDAExam,
            without_do: shipmentsWithoutDO,
            pending_review: shipmentsPendingReview,
            missing_info: shipmentsMissingInfo,
          }}
          countsReady={!loading.needUploads && !loading.exam && !loading.withoutDO}
          token={token}
          apiBase={URL}
        />

        <div className="data-table">
          {loading.shipments ? (
            <LoadingTable />
          ) : (
            <>
              <ShipmentsTableSearch
                shipments={shipments}
                filterState={filterState}
                setFilterState={setFilterState}
                statusOptions={homeShipmentStatusOptions}
              />

              <div className="pagination">
                {page > 1 && (
                  <button onClick={gotoPreviousPage}>{"< Previous"}</button>
                )}
                <span className="text">{`${shown}/${total}`}</span>
                {page < last && (
                  <button onClick={gotoNextPage}>{"Next >"}</button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;
